import { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import DailyOrderVolumeByDepot from "./daily-order-volume-by-depot.tsx";
import DailyOrderVolumeByCustomer from "./daily-order-volume-by-customer.tsx";
import NewCustomersDaily from "./new-customers-daily.tsx";
import RevenueByCustomer from "./revenue-by-customer.tsx";
import DateRangePicker from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";
import {
  useDailyOrderVolumeByCustomer,
  useDailyRevenueByCustomer,
  useNewCustomersDaily,
} from "@/api/analytics";

export default function AnalyticsDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date(),
  });

  // Fetch data for summary cards
  const { data: orderVolumeData } = useDailyOrderVolumeByCustomer(dateRange);
  const { data: revenueData } = useDailyRevenueByCustomer(dateRange);
  const { data: newCustomersData } = useNewCustomersDaily(dateRange);

  // Calculate summary totals
  const totalOrders = useMemo(() => {
    if (!orderVolumeData || !Array.isArray(orderVolumeData)) return 0;
    return orderVolumeData.reduce((sum, item) => sum + item.volume, 0);
  }, [orderVolumeData]);

  const totalNewCustomers = useMemo(() => {
    if (!newCustomersData || !Array.isArray(newCustomersData)) return 0;
    return newCustomersData.reduce((sum, item) => sum + item.count, 0);
  }, [newCustomersData]);

  const totalRevenue = useMemo(() => {
    if (!revenueData || !Array.isArray(revenueData)) return 0;
    return revenueData.reduce((sum, item) => sum + item.revenue, 0);
  }, [revenueData]);

  // Format for currency display
  const formattedTotalRevenue = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(totalRevenue);
  }, [totalRevenue]);

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <h1 className="text-3xl font-bold truncate">Analytics Dashboard</h1>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <DateRangePicker date={dateRange} onDateChange={setDateRange} />
        </div>
      </div>

      {/* Summary Section - Moved to top for better visibility */}
      <h2 className="text-2xl font-semibold mb-4">Summary</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Total Orders</CardTitle>
            <CardDescription>Period total</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl md:text-4xl font-bold break-words">
              {totalOrders}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">New Customers</CardTitle>
            <CardDescription>Period total</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl md:text-4xl font-bold break-words">
              {totalNewCustomers}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Total Revenue</CardTitle>
            <CardDescription>Period total</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl md:text-4xl font-bold break-words overflow-hidden text-ellipsis">
              {formattedTotalRevenue}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        {/* Order Volume Section */}
        <h2 className="text-2xl font-semibold mb-4">Order Volume</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg truncate">
                Daily Order Volume by Depot
              </CardTitle>
              <CardDescription className="text-sm line-clamp-2">
                Number of orders processed per day by each depot
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-hidden">
              <div className="w-full h-[300px] md:h-[350px]">
                <DailyOrderVolumeByDepot dateRange={dateRange} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg truncate">
                Daily Order Volume by Customer
              </CardTitle>
              <CardDescription className="text-sm line-clamp-2">
                Number of orders placed per day by each customer
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-hidden">
              <div className="w-full h-[300px] md:h-[350px]">
                <DailyOrderVolumeByCustomer dateRange={dateRange} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customers Section */}
        <h2 className="text-2xl font-semibold mb-4">Customers</h2>
        <Card className="mb-8">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg truncate">New Customers</CardTitle>
            <CardDescription className="text-sm line-clamp-2">
              Number of new customers registered per day
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-hidden">
            <div className="w-full h-[300px] md:h-[400px]">
              <NewCustomersDaily dateRange={dateRange} />
            </div>
          </CardContent>
        </Card>

        {/* Revenue Section */}
        <h2 className="text-2xl font-semibold mb-4">Revenue</h2>
        <Card className="mb-8">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg truncate">
              Revenue by Customer
            </CardTitle>
            <CardDescription className="text-sm line-clamp-2">
              Revenue generated by customers over time
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-hidden">
            <div className="w-full h-[350px] md:h-[500px]">
              <RevenueByCustomer dateRange={dateRange} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
